import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableFooter,
  TablePagination,
  CircularProgress,
  Button,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { keyframes } from "@mui/system";
import {
  PurchaseOrdersProvider,
  usePurchaseOrders,
} from "../../contexts/albina/PurchaseOrdersContext";
import * as XLSX from "xlsx";
import STORE_NAMES, { getStoreFromUrl } from "../../utils/STORE_NAMES";
import { resolveStoreName } from "../../utils/resolveStoreName";
// endpoints to resolve store names
const endpoint0 = process.env.REACT_APP_PURCHASE_ORDERS_ENDPOINT_0;
const endpoint1 = process.env.REACT_APP_PURCHASE_ORDERS_ENDPOINT_1;
const endpoint2 = process.env.REACT_APP_PURCHASE_ORDERS_ENDPOINT_2;

const baseUrl = process.env.REACT_APP_BASE_URL;


// ---------------- styles
const floatIn = keyframes`
  0% { transform: translateY(8px); opacity: 0; }
  60% { transform: translateY(-6px); opacity: 1; }
  100% { transform: translateY(0); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const Root = styled("div")(({ theme }) => ({
  minHeight: "100%",
  padding: theme.spacing(3),
  background:
    "radial-gradient(circle at 10% 10%, rgba(6,182,212,0.06), transparent 10%), radial-gradient(circle at 90% 90%, rgba(99,102,241,0.04), transparent 10%), linear-gradient(180deg, rgba(246,248,255,0.6), rgba(250,250,255,0.94))",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
}));

const Title = styled("h2")(({ theme }) => ({
  margin: 0,
  fontSize: "1.6rem",
  letterSpacing: "-0.2px",
  background: "linear-gradient(90deg, #06b6d4, #6366f1)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundSize: "200% 100%",
  animation: `${shimmer} 3s linear infinite`,
  fontWeight: 700,
}));

const GlassPaper = styled(Paper)(({ theme }) => ({
  borderRadius: 12,
  background:
    "linear-gradient(180deg, rgba(255,255,255,0.8), rgba(250,250,255,0.9))",
  boxShadow: "0 8px 30px rgba(2,6,23,0.04)",
  padding: theme.spacing(2),
}));

// ---------------- helper: build row for a PO line
function buildLine(po, item, goodsList, suppliersList) {
  const goodObj = goodsList?.find((g) => String(g?._id) === String(item.good));
  const supplier = suppliersList?.find(
    (s) => String(s?._id) === String(item.supplier)
  );
  const qty = Number(item.quantity) || 0;
  const unitPrice = Number(item.price_used || 0);
  const subtotal = qty * unitPrice;
  const tax = Number(item.total_tax_used || 0);
  const total = subtotal + tax;

  return {
    poRef:
      po.order_no ||
      po.invoice_no ||
      (po._id ? `#${String(po?._id).slice(0, 8)}` : "N/A"),
    poId: po?._id,
    date: po.received_date || po.orderDate || null,
    supplierName: supplier?.name || item.supplier || "N/A",
    goodName: goodObj?.name || item.good,
    qty,
    unit: goodObj?.measurement_unit || "N/A",
    unitPrice,
    subtotal,
    tax,
    total,
    storeIndex: po.store,
  };
}

// ---------------- main component
const PurchasesByUnit = () => {
  const {
    purchaseOrders = [],
    purchaseOrders1 = [],
    purchaseOrders2 = [],
    purchaseOrders3 = [],
    purchaseOrders4 = [],
    purchaseOrders5 = [],
    purchaseOrders6 = [],
    purchaseOrders7 = [],

    goods = [],
    suppliers = [],
    loading,
    error,
  } = usePurchaseOrders();
  const endpoint0 = process.env.REACT_APP_PURCHASE_ORDERS_ENDPOINT_0;
  const endpoint1 = process.env.REACT_APP_PURCHASE_ORDERS_ENDPOINT_1;
  const endpoint2 = process.env.REACT_APP_PURCHASE_ORDERS_ENDPOINT_2;
  const endpoint3 = process.env.REACT_APP_PURCHASE_ORDERS_ENDPOINT_3;
  const endpoint4 = process.env.REACT_APP_PURCHASE_ORDERS_ENDPOINT_4;
  const endpoint5 = process.env.REACT_APP_PURCHASE_ORDERS_ENDPOINT_5;
  const endpoint6 = process.env.REACT_APP_PURCHASE_ORDERS_ENDPOINT_6;
  const endpoint7 = process.env.REACT_APP_PURCHASE_ORDERS_ENDPOINT_7;

  const getStoreFromUrl = (url) => {
    try {
      const params = new URL(url).searchParams;
      return params.get("0_store");
    } catch (err) {
      console.error("Invalid URL:", err);
      return null;
    }
  };

  const storeId0 = getStoreFromUrl(endpoint0);
  const storeId1 = getStoreFromUrl(endpoint1);
  const storeId2 = getStoreFromUrl(endpoint2);
  const storeId3 = getStoreFromUrl(endpoint3);
  const storeId4 = getStoreFromUrl(endpoint4);
  const storeId5 = getStoreFromUrl(endpoint5);
  const storeId6 = getStoreFromUrl(endpoint6);
  const storeId7 = getStoreFromUrl(endpoint7);

  const storeName0 = STORE_NAMES[storeId0] || "Unknown Store";
  const storeName1 = STORE_NAMES[storeId1] || "Unknown Store";
  const storeName2 = STORE_NAMES[storeId2] || "Unknown Store";
  const storeName3 = STORE_NAMES[storeId3] || "Unknown Store";
  const storeName4 = STORE_NAMES[storeId4] || "Unknown Store";
  const storeName5 = STORE_NAMES[storeId5] || "Unknown Store";
  const storeName6 = STORE_NAMES[storeId6] || "Unknown Store";
  const storeName7 = STORE_NAMES[storeId7] || "Unknown Store";

  const allPOs = useMemo(
    () => [
      ...(purchaseOrders || []),
      ...(purchaseOrders1 || []),
      ...(purchaseOrders2 || []),
      ...(purchaseOrders3 || []),
      ...(purchaseOrders4 || []),
      ...(purchaseOrders5 || []),
      ...(purchaseOrders6 || []),
      ...(purchaseOrders7 || []),
    ],
    [
      purchaseOrders,
      purchaseOrders1,
      purchaseOrders2,
      purchaseOrders3,
      purchaseOrders4,
      purchaseOrders5,
      purchaseOrders6,
      purchaseOrders7,
    ]
  );

  // collect unique measurement units
  const allUnits = useMemo(() => {
    const units = new Set(goods.map((g) => g.measurement_unit).filter(Boolean));
    return Array.from(units).sort();
  }, [goods]);

  const allStoreNames = useMemo(() => Object.values(STORE_NAMES), []);
  const [selectedUnit, setSelectedUnit] = useState("");
  const [storeFilter, setStoreFilter] = useState("");
  const [search, setSearch] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const getStoreNameForPO = (po) => {
    const names = [
      "BOON COFFEE ROASTERS CAFE-FALAK",
      "BOON COFFEE ROASTERS CAFE LLC - JUMEIRAH PARK",
      "BOON COFFEE ROASTERS CAFE - PADEL CLUB",
      "BOON COFFEE ROASTERS FZCO - GOLDEN MILE",
      "BOON COFFEE ROASTERS CAFÉ LLC - MEADOWS VILLAGE",
      "BOON COFFEE ROASTERS CAFÉ LLC (BRANCH) - FORTE TOWER",
      "BOON PRODUCTION KITCHEN",
      "Dubai Hills",
    ];
    return names[po.storeIndex] || "Unknown Store";
  };

  const rows = useMemo(() => {
    if (!selectedUnit) return [];

    const matched = [];
    allPOs.forEach((po) => {
      if (!Array.isArray(po.po_inv_items)) return;
      po.po_inv_items.forEach((item) => {
        const goodObj = goods.find((g) => String(g?._id) === String(item.good));
        // console.log(JSON.stringify(goodObj));
        if (goodObj?.measurement_unit === selectedUnit) {
          const line = buildLine(po, item, goods, suppliers);

          // attach store name
          line.storeName = getStoreNameForPO(po);
          matched.push(line);
        }
      });
      //alert(JSON.stringify(allPOs))
    });

    // apply store filter
    const byStore = storeFilter
      ? matched.filter((r) => r.storeName === storeFilter)
      : matched;

    // apply date filters
    const fromDate = filterFrom ? new Date(filterFrom) : null;
    const toDate = filterTo ? new Date(filterTo) : null;
    const byDate = byStore.filter((r) => {
      if (!r.date) return true;
      const d = new Date(r.date);
      if (fromDate && d < fromDate) return false;
      if (toDate && d > toDate) return false;
      return true;
    });

    // apply search
    const q = (search || "").trim().toLowerCase();
    const bySearch = q
      ? byDate.filter(
          (r) =>
            (r.poRef || "").toString().toLowerCase().includes(q) ||
            (r.supplierName || "").toString().toLowerCase().includes(q) ||
            (r.goodName || "").toString().toLowerCase().includes(q) ||
            (r.storeName || "").toString().toLowerCase().includes(q)
        )
      : byDate;

    // sort newest first
    bySearch.sort((a, b) => {
      const da = a.date ? new Date(a.date).getTime() : 0;
      const db = b.date ? new Date(b.date).getTime() : 0;
      return db - da;
    });

    return bySearch;
  }, [
    selectedUnit,
    allPOs,
    goods,
    suppliers,
    storeFilter,
    filterFrom,
    filterTo,
    search,
  ]);

  const footerTotals = useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        acc.qty += r.qty;
        acc.subtotal += r.subtotal;
        acc.tax += r.tax;
        acc.total += r.total;
        return acc;
      },
      { qty: 0, subtotal: 0, tax: 0, total: 0 }
    );
  }, [rows]);

  const pageRows = rows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const exportToExcel = () => {
    if (!selectedUnit) return;
    const sheetRows = rows.map((r) => ({
      "PO Ref": r.poRef,
      Date: r.date,
      Store: r.storeName,
      Supplier: r.supplierName,
      Good: r.goodName,
      Quantity: r.qty,
      Unit: r.unit,
      "Unit Price": r.unitPrice.toFixed(2),
      Subtotal: r.subtotal.toFixed(2),
      Tax: r.tax.toFixed(2),
      Total: r.total.toFixed(2),
    }));
    const ws = XLSX.utils.json_to_sheet(sheetRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Purchases by Unit");
    XLSX.writeFile(
      wb,
      `Purchases_by_Unit_${selectedUnit}_filtered_${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`
    );
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="260px"
      >
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return <Typography color="error">Error: {String(error)}</Typography>;
  }

  return (
    <Root>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
      >
        <div>
          <Title>Purchases by Measurement Unit</Title>
          <Typography variant="body2" color="text.secondary">
            Select a measurement unit to list all goods purchased with it.
          </Typography>
        </div>
        <Button
          variant="outlined"
          disabled={!selectedUnit || rows.length === 0}
          onClick={exportToExcel}
        >
          Export Excel
        </Button>
      </Box>

      <GlassPaper>
        <Box mb={2}>
          <TextField
            label="Measurement Unit"
            select
            size="small"
            sx={{ minWidth: 280 }}
            value={selectedUnit}
            onChange={(e) => {
              setSelectedUnit(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="">-- Select Unit --</MenuItem>
            {allUnits.map((u) => (
              <MenuItem key={u} value={u}>
                {u}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
          <TextField
            label="Store (filter)"
            select
            size="small"
            sx={{ minWidth: 260 }}
            value={storeFilter}
            onChange={(e) => {
              setStoreFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="">All stores</MenuItem>
            {allStoreNames.map((name) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="From"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={filterFrom}
            onChange={(e) => {
              setFilterFrom(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 160 }}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={filterTo}
            onChange={(e) => {
              setFilterTo(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 160 }}
          />

          <TextField
            label="Search (PO ref / supplier / good / store)"
            size="small"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 260 }}
          />
        </Box>

        <TableContainer
          component={Paper}
          sx={{ animation: `${floatIn} 400ms ease both` }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>PO Ref</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Store</TableCell>
                <TableCell>Supplier</TableCell>
                <TableCell>Good</TableCell>
                <TableCell align="right">Quantity</TableCell>
                <TableCell>Unit</TableCell>
                <TableCell align="right">Unit Price</TableCell>
                <TableCell align="right">Subtotal</TableCell>
                <TableCell align="right">Tax</TableCell>
                <TableCell align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pageRows.map((r, idx) => (
                <TableRow key={`${r?.poId}-${idx}`}>
                  <TableCell>{r.poRef}</TableCell>
                  <TableCell>{r.date || "N/A"}</TableCell>
                  <TableCell>{r.storeName}</TableCell>
                  <TableCell>{r.supplierName}</TableCell>
                  <TableCell>{r.goodName}</TableCell>
                  <TableCell align="right">{r.qty}</TableCell>
                  <TableCell>{r.unit}</TableCell>
                  <TableCell align="right">{r.unitPrice.toFixed(2)}</TableCell>
                  <TableCell align="right">{r.subtotal.toFixed(2)}</TableCell>
                  <TableCell align="right">{r.tax.toFixed(2)}</TableCell>
                  <TableCell align="right">{r.total.toFixed(2)}</TableCell>
                </TableRow>
              ))}
              {pageRows.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={11}
                    align="center"
                    style={{ padding: 24 }}
                  >
                    {selectedUnit
                      ? "No purchases found for this unit (with current filters)."
                      : "Select a measurement unit to start."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell
                  colSpan={5}
                  style={{ fontWeight: 700, textAlign: "right" }}
                >
                  Totals:
                </TableCell>
                <TableCell align="right" style={{ fontWeight: 700 }}>
                  {footerTotals.qty}
                </TableCell>
                <TableCell />
                <TableCell />
                <TableCell align="right" style={{ fontWeight: 700 }}>
                  {footerTotals.subtotal.toFixed(2)}
                </TableCell>
                <TableCell align="right" style={{ fontWeight: 700 }}>
                  {footerTotals.tax.toFixed(2)}
                </TableCell>
                <TableCell align="right" style={{ fontWeight: 700 }}>
                  {footerTotals.total.toFixed(2)}
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>

        <Box display="flex" justifyContent="flex-end" mt={2}>
          <TablePagination
            component="div"
            count={rows.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 20, 50]}
          />
        </Box>
      </GlassPaper>
    </Root>
  );
};

const PurchasesByUnitPage = () => (
  <PurchaseOrdersProvider
   endpoint0={`${baseUrl}/api/purchase-orders/purchase-orders-api-0`}
      endpoint1={`${baseUrl}/api/purchase-orders/purchase-orders-api-1`}
      endpoint2={`${baseUrl}/api/purchase-orders/purchase-orders-api-2`}
      endpoint3={`${baseUrl}/api/purchase-orders/purchase-orders-api-3`}
      endpoint4={`${baseUrl}/api/purchase-orders/purchase-orders-api-4`}
      endpoint5={`${baseUrl}/api/purchase-orders/purchase-orders-api-5`}
      endpoint6={`${baseUrl}/api/purchase-orders/purchase-orders-api-6`}
      endpoint7={`${baseUrl}/api/purchase-orders/purchase-orders-api-7`}
  >
    <PurchasesByUnit />
  </PurchaseOrdersProvider>
);

export default PurchasesByUnitPage;
